import developerNarrationManifest from '@/_data/developerPrimitiveNarrationManifest';
import narrationConfig from '@/_data/narrationConfig.json' with { type: 'json' };
import narrationManifest from '@/_data/primitiveNarrationManifest';
import { conceptPresentationAt, episodeBeatSection, episodeSubtitleSections, planAnchorIndex, planBeatOpeningIndex, isSameEpisodePlaybackExecution, reduceEpisodeSubtitleIntent, resolveEpisodeBeatSelection, withSceneSubtitle } from '@/_pure/episode';
import { toPrimitiveOperationInputDraft } from '@/adapters/primitive/_pure/interaction/operationInput';
import { toOperationSummary } from '@/adapters/primitive/_pure/interaction/overlay';
import { createInitialPrimitiveRuntimeState, requestPrimitiveRewind } from '@/adapters/primitive/_pure/model/runtimeState';
import { reportEpisodeDiagnostic } from '@/episodeDiagnostics';
import { readCosmosDiagnostics } from '@/diagnosticsState';
import { createBrowserEpisodeNarrationOwner } from '@/episodeNarration';
import { createEpisodePlaybackEffectOwner } from '@/episodePlayback';
import { detachPrimitiveEpisode, primitiveConceptValue } from '@/adapters/primitive/_pure/episode';
import { createPrimitiveScenePlanResolver, primitiveDeveloperEpisodes, primitiveEpisodes, resolvePrimitiveEpisode } from '@/adapters/primitive/episodeCatalog';
import { resolveScenePlanAudition } from '@/adapters/primitive/scenePlanAuditions';
import type { SpaceAdapterRuntimeController } from '@/_contract/types';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/primitive/_contract/config';

type Controller = SpaceAdapterRuntimeController<Config, RuntimeState, CosmosDataModel>;
type Args = { config: Config; runtimeState: RuntimeState; cosmosData: CosmosDataModel };

export function createEpisodeController(base: Controller): Controller {
    const resolve = createPrimitiveScenePlanResolver();
    const enterScene = (args: Args, handle: string, sceneIndex: number,
        automatic = false): RuntimeState => {
        const { scene } = resolvePrimitiveEpisode(handle, sceneIndex);
        const previous = args.runtimeState;
        const episode = { episodeHandle: handle, beatIndex: 0, status: 'reading' as const, cueIndex: 0,
            playbackAdvanceMode: automatic ? 'automatic' as const : 'manual' as const, isPlaying: automatic,
            soundEnabled: previous.episode?.soundEnabled ?? args.config.episodeSoundDefault ?? false,
            subtitlesEnabled: previous.episode?.subtitlesEnabled ?? true,
            ...(previous.episode?.subtitleTrace ? { subtitleTrace: previous.episode.subtitleTrace } : {}),
            revision: (previous.episode?.revision ?? 0) + 1 };
        if (scene.sceneKind === 'concept') {
            const { arrangement } = conceptPresentationAt(scene.beats[0]!, episode);
            return { ...createInitialPrimitiveRuntimeState('natural-identity'),
                inputDraft: { operation: 'natural-identity', inputText: String(primitiveConceptValue(arrangement)) },
                conceptArrangement: arrangement, sceneIndex,
                explorationBeforeConcept: previous.explorationBeforeConcept ?? (previous.episode
                    ? { ...previous, episode: undefined } : previous),
                episode };
        }
        const { trace, openingStepIndex, directions } = resolve(args.config, handle, sceneIndex);
        return advance({ ...args, runtimeState: {
            ...createInitialPrimitiveRuntimeState(trace.payload.input.operation), selectedTrace: trace,
            inputDraft: toPrimitiveOperationInputDraft(trace.payload.input), sceneIndex,
            committedStepIndex: openingStepIndex,
            divisionComparison: scene.divisionComparison,
            fullComparisons: scene.fullComparisons,
            illustrativeQuotientBands: scene.illustrativeQuotientBands,
            sceneDirectionCatalogs: directions,
            episode: { ...episode, status: 'moving' },
        } });
    };
    const advance = (args: Args): RuntimeState => {
        const state = args.runtimeState;
        const playback = state.episode;
        if (!playback || state.pendingStepIndex !== null) return state;
        const { scene } = resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
        if (scene.sceneKind === 'concept') {
            const beat = scene.beats[playback.beatIndex];
            if (!beat) throw new Error('Missing Primitive concept beat.');
            const { arrangement } = conceptPresentationAt(beat, playback);
            return { ...state, conceptArrangement: arrangement,
                inputDraft: { operation: 'natural-identity', inputText: String(primitiveConceptValue(arrangement)) },
                episode: { ...playback, status: 'reading' } };
        }
        const { plan, trace, replay } = resolve(args.config, playback.episodeHandle, state.sceneIndex ?? 0);
        if (state.selectedTrace !== trace) throw new Error('Primitive Episode lost its referenced trace.');
        const beat = plan.beats[playback.beatIndex];
        let targetCueIndex = playback.cueIndex ?? 0;
        const continuous = scene.beats[playback.beatIndex]?.continuousCues;
        while (beat?.cues?.[targetCueIndex] && continuous?.includes(beat.cues[targetCueIndex]!.id)) targetCueIndex++;
        const cue = beat?.cues?.[targetCueIndex];
        const target = cue ? planAnchorIndex(replay, cue)
            : replay.landmarks.findIndex((landmark) => landmark.id === beat?.landmark);
        if (target < 0) throw new Error('Primitive Episode requires an exact replay landmark.');
        if (state.committedStepIndex === target) {
            return { ...state, playbackAdvanceMode: 'manual', isPlaying: false, episode: { ...playback, status: 'reading' } };
        }
        const next = base.reduceOverlayIntent?.({ ...args, runtimeState: { ...state, playbackAdvanceMode: 'manual', isPlaying: false },
            intent: { overlayIntentKind: 'action-triggered', actionId: state.committedStepIndex < target ? 'primitive-next' : 'primitive-prev' } });
        if (!next || (next.pendingStepIndex === null && next.committedStepIndex === state.committedStepIndex)) {
            throw new Error('Primitive Episode could not advance to its landmark.');
        }
        if (next.pendingStepIndex !== null && target > next.pendingStepIndex
            && targetCueIndex > (playback.cueIndex ?? 0)) {
            return { ...next, pendingStepIndex: target, episodeVisualStepIndex: state.committedStepIndex };
        }
        // Prev follows the existing immediate retreat semantics; forward movement awaits each transition completion.
        return next.pendingStepIndex === null ? advance({ ...args, runtimeState: next }) : next;
    };
    const move = (args: Args, beatIndex: number, immediate = false): RuntimeState => {
        const playback = args.runtimeState.episode;
        if (!playback) return args.runtimeState;
        const index = args.runtimeState.sceneIndex ?? 0;
        const { episode, scene } = resolvePrimitiveEpisode(playback.episodeHandle, index);
        if (beatIndex >= scene.beats.length && index < episode.scenes.length - 1) {
            const next = enterScene(args, playback.episodeHandle, index + 1, playback.isPlaying);
            const selected = { ...next, episode: next.episode ? { ...next.episode,
                narrationRequested: playback.narrationRequested } : undefined };
            return immediate ? move({ ...args, runtimeState: selected }, 0, true) : selected;
        }
        if (beatIndex < 0 && index > 0) {
            const previous = enterScene(args, playback.episodeHandle, index - 1);
            const beats = episode.scenes[index - 1]!.beats;
            return move({ ...args, runtimeState: { ...previous, episode: previous.episode
                ? { ...previous.episode, narrationRequested: playback.narrationRequested } : undefined } }, beats.length - 1, immediate);
        }
        if (beatIndex < 0 || beatIndex >= scene.beats.length) {
            return { ...args.runtimeState, playbackAdvanceMode: 'manual', isPlaying: false,
                episode: { ...playback, status: 'reading', isPlaying: false, narrationRequested: false } };
        }
        let selected: RuntimeState = { ...args.runtimeState, playbackAdvanceMode: 'manual', isPlaying: false,
            episode: { ...playback, beatIndex, status: 'moving', narrationCompleted: false,
                visualRequested: true, cueIndex: 0, narrationCueReached: undefined, subtitleIndex: undefined,
                revision: playback.revision + 1, error: undefined } };
        if (immediate || (scene.sceneKind === 'trace' && scene.beats[beatIndex]!.replayFrom)) {
            selected = { ...selected, pendingStepIndex: null, episodeVisualStepIndex: undefined, booleanAssignment: undefined,
                booleanAssignmentQueue: undefined };
            if (scene.sceneKind === 'trace') {
                const { plan, replay, openingStepIndex } = resolve(args.config, playback.episodeHandle, index);
                const target = planBeatOpeningIndex(plan, replay, beatIndex, openingStepIndex);
                const destination = replay.landmarks.findIndex(landmark => landmark.id === plan.beats[beatIndex]?.landmark);
                const state = args.runtimeState;
                // A replay reached from its own destination rewinds visibly; any other entry jumps to its opening.
                const rewinds = scene.beats[beatIndex]!.replayFrom !== undefined && state.pendingStepIndex === null
                    && state.booleanAssignment === undefined
                    && target < state.committedStepIndex && state.committedStepIndex <= destination;
                selected = rewinds ? requestPrimitiveRewind(selected, target) : { ...selected, committedStepIndex: target };
            }
        }
        return advance({ ...args, runtimeState: selected });
    };
    return {
        ...base,
        reducePlaybackSuspension: (args) => {
            const next = base.reducePlaybackSuspension?.(args) ?? args.runtimeState;
            return next.episode ? { ...next, episode: { ...next.episode,
                isPlaying: !args.suspended && args.resumeState.episode?.isPlaying === true,
            } } : next;
        },
        buildOverlayModel: (args) => {
            const model = base.buildOverlayModel?.(args);
            if (!model) return model;
            const state = args.runtimeState;
            const playback = state.episode;
            const busy = state.pendingStepIndex !== null || state.booleanAssignment !== undefined;
            const resolved = playback ? resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0) : undefined;
            const episode = resolved?.episode;
            const scene = resolved?.scene;
            const beat = scene?.beats[playback?.beatIndex ?? 0];
            const canNext = Boolean(playback && episode && scene && (
                playback.beatIndex < scene.beats.length - 1
                || (state.sceneIndex ?? 0) < episode.scenes.length - 1));
            return { ...model, title: episode?.title ?? model.title,
                ...(playback && episode && scene && beat ? { timeline: {
                    label: 'Episode timeline', positionSectionId: 'episode-beat', controlsSectionId: 'primitive-episode-playback',
                } } : {}),
                sections: [
                { sectionKind: 'select', sectionId: 'primitive-episode', label: 'Episode',
                    selectedValue: playback?.episodeHandle ?? '', enabled: !busy && !playback?.isPlaying && !state.isPlaying,
                    options: [{ value: '', label: 'Normal primitive playback' },
                        ...primitiveEpisodes.map((episode) => ({ value: episode.handle, label: episode.title }))] },
                ...(playback && episode && scene && beat ? [
                    episodeBeatSection(episode.scenes, state.sceneIndex ?? 0, playback.beatIndex),
                    ...(scene.sceneKind === 'trace' ? [{
                    sectionKind: 'actions' as const, sectionId: 'primitive-episode-expression',
                    label: 'Expression', actions: [],
                    description: toOperationSummary(resolve(args.config, playback.episodeHandle, state.sceneIndex ?? 0).trace.payload.input),
                }] : []), ...episodeSubtitleSections(playback), {
                    sectionKind: 'actions' as const, sectionId: 'primitive-episode-playback',
                    label: `${scene.title} (${(state.sceneIndex ?? 0) + 1}/${episode.scenes.length}; ${playback.beatIndex + 1}/${scene.beats.length})`,
                    actions: [
                        { actionId: 'episode-play-pause', playbackRole: 'toggle' as const,
                            label: playback.isPlaying ? 'Pause' : 'Play', isActive: playback.isPlaying,
                            enabled: playback.isPlaying || canNext || (scene.sceneKind === 'trace' && scene.excerpt !== undefined) },
                        { actionId: 'episode-reset', playbackRole: 'reset' as const, label: 'Reset', enabled: true },
                        { actionId: 'episode-prev', playbackRole: 'step' as const, label: 'Prev',
                            allowDuringPlayback: true,
                            enabled: playback.beatIndex > 0 || (state.sceneIndex ?? 0) > 0 },
                        { actionId: 'episode-next', playbackRole: 'step' as const, label: 'Next',
                            allowDuringPlayback: true,
                            enabled: canNext
                                || (primitiveEpisodes.findIndex((entry) => entry.handle === playback.episodeHandle) >= 0
                                    && primitiveEpisodes.findIndex((entry) => entry.handle === playback.episodeHandle) < primitiveEpisodes.length - 1) },
                    ],
                },
                ...(playback.error ? [{
                    sectionKind: 'actions' as const, sectionId: 'primitive-episode-error',
                    label: 'Episode error', actions: [], description: playback.error,
                }] : [])] : model.sections),
            ], ...withSceneSubtitle(playback, beat) };
        },
        reduceOverlayIntent: (args) => {
            const state = args.runtimeState;
            const { intent } = args;
            if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'primitive-episode') {
                if (state.pendingStepIndex !== null || state.isPlaying || state.booleanAssignment || state.episode?.isPlaying) return state;
                if (intent.value === '') return detachPrimitiveEpisode(state, args.config);
                return enterScene({ ...args, runtimeState: { ...state, episode: undefined } }, intent.value, 0);
            }
            const playback = state.episode;
            if (!playback) return base.reduceOverlayIntent?.(args) ?? state;
            if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-beat') {
                const { episode } = resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
                const target = resolveEpisodeBeatSelection(episode.scenes, intent.value);
                const selected = target.sceneIndex === (state.sceneIndex ?? 0) ? state
                    : enterScene(args, playback.episodeHandle, target.sceneIndex);
                return move({ ...args, runtimeState: { ...selected, episode: { ...selected.episode!,
                    playbackAdvanceMode: 'manual', narrationRequested: true, isPlaying: false } } }, target.beatIndex, true);
            }
            if (intent.overlayIntentKind === 'checkbox-changed' && intent.sectionId === 'episode-sound') {
                const next = { ...state, episode: { ...playback, soundEnabled: intent.checked } };
                // An error (such as a missing recording) restarts the beat once sound no longer requires the recording.
                return playback.error ? move({ ...args, runtimeState: { ...next, episode: { ...next.episode,
                    narrationRequested: true, isPlaying: false } } }, playback.beatIndex) : next;
            }
            const subtitleState = reduceEpisodeSubtitleIntent(playback, intent);
            if (subtitleState) return { ...state, episode: subtitleState };
            if (intent.overlayIntentKind !== 'action-triggered') return state;
            if (intent.actionId === 'episode-cue-continue') {
                const { scene } = resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
                const beat = scene.beats[playback.beatIndex];
                const cue = beat?.cues?.[playback.cueIndex ?? 0];
                if (scene.sceneKind === 'trace' && cue && 'landmark' in cue
                    && scene.beats[playback.beatIndex]?.continuousCues?.includes(cue.id)) {
                    const { replay } = resolve(args.config, playback.episodeHandle, state.sceneIndex ?? 0);
                    if ((playback.narrationCueReached ?? -1) < (playback.cueIndex ?? 0)
                        || (state.episodeVisualStepIndex ?? state.committedStepIndex) < planAnchorIndex(replay, cue)) return state;
                    return advance({ ...args, runtimeState: { ...state, episode: { ...playback,
                        cueIndex: (playback.cueIndex ?? 0) + 1 } } });
                }
                if (!('cues' in beat) || !beat.cues?.[playback.cueIndex ?? 0]
                    || (playback.narrationCueReached ?? -1) < (playback.cueIndex ?? 0)
                    || playback.status !== 'reading' || state.pendingStepIndex !== null) return state;
                return advance({ ...args, runtimeState: { ...state, episode: { ...playback,
                    cueIndex: (playback.cueIndex ?? 0) + 1, status: 'moving' } } });
            }
            if (intent.actionId === 'episode-reset') {
                return enterScene(args, playback.episodeHandle, 0);
            }
            if (intent.actionId === 'episode-play-pause') {
                // Playing continues the current timeline; only an error restarts the beat.
                if (playback.error && !playback.isPlaying) {
                    return move({ ...args, runtimeState: { ...state, episode: { ...playback,
                        playbackAdvanceMode: 'automatic', isPlaying: true } } }, playback.beatIndex);
                }
                return { ...state, episode: { ...playback, playbackAdvanceMode: 'automatic',
                    visualRequested: true, isPlaying: !playback.isPlaying } };
            }
            if (intent.actionId === 'episode-next' || intent.actionId === 'episode-prev' || intent.actionId === 'episode-continue') {
                if (intent.actionId === 'episode-continue' && state.pendingStepIndex !== null) return state;
                return move({ ...args, runtimeState: { ...state,
                    episode: { ...playback,
                        playbackAdvanceMode: intent.actionId === 'episode-continue' ? playback.playbackAdvanceMode : 'manual',
                        narrationRequested: intent.actionId !== 'episode-continue',
                        isPlaying: intent.actionId === 'episode-continue' && playback.isPlaying } } },
                playback.beatIndex + (intent.actionId === 'episode-prev' ? -1 : 1), intent.actionId !== 'episode-continue');
            }
            return state;
        },
        reduceDerivationTransitionPhaseComplete: (args) => {
            const state = args.runtimeState;
            const playback = state.episode;
            if (!playback || state.pendingStepIndex === null) return state;
            const { scene: selectedScene } = resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
            if (selectedScene.sceneKind !== 'trace'
                || !selectedScene.beats[playback.beatIndex]?.continuousCues?.length) return state;
            const { scene, replay, steps } = resolve(args.config, playback.episodeHandle, state.sceneIndex ?? 0);
            const beat = scene.beats[playback.beatIndex];
            for (const cue of beat?.cues ?? []) {
                if (!beat.continuousCues?.includes(cue.id)) continue;
                const index = planAnchorIndex(replay, cue);
                const step = steps[index];
                if (!step || index <= (state.episodeVisualStepIndex ?? state.committedStepIndex)
                    || index > state.pendingStepIndex
                    || args.transitionKey !== `${step.stepKey}:${step.keyframes.length - 1}`) continue;
                return { ...state, episodeVisualStepIndex: index };
            }
            return state;
        },
        reduceDerivationTransitionComplete: (args) => {
            reportEpisodeDiagnostic('transition-completed', () => ({
                committedStepIndex: args.runtimeState.committedStepIndex,
                pendingStepIndex: args.runtimeState.pendingStepIndex, beatIndex: args.runtimeState.episode?.beatIndex }));
            const completed = base.reduceDerivationTransitionComplete?.(args) ?? args.runtimeState;
            const next = completed.episodeVisualStepIndex === undefined ? completed
                : { ...completed, episodeVisualStepIndex: undefined };
            return next.episode?.status === 'moving' ? advance({ ...args, runtimeState: next }) : next;
        },
    };
}

export function createEpisodeEffectOwner(controller: Controller, narration = createBrowserEpisodeNarrationOwner()) {
    let diagnosticSignature: string | undefined;
    const resolve = createPrimitiveScenePlanResolver();
    return createEpisodePlaybackEffectOwner({
        select: (args: Args & { playbackActive?: boolean; animationSuspended?: boolean }) => {
            const playback = args.runtimeState.episode!;
            const { scene } = resolvePrimitiveEpisode(playback.episodeHandle, args.runtimeState.sceneIndex ?? 0);
            const beat = scene.beats[playback.beatIndex];
            const diagnostic = () => ({ episodeHandle: playback.episodeHandle, sceneId: scene.id,
                beatId: beat?.id, cueIndex: playback.cueIndex, narrationCueReached: playback.narrationCueReached,
                status: playback.status, soundEnabled: playback.soundEnabled,
                committedStepIndex: args.runtimeState.committedStepIndex, pendingStepIndex: args.runtimeState.pendingStepIndex,
                playbackActive: args.playbackActive, animationSuspended: args.animationSuspended });
            if (readCosmosDiagnostics().episode) {
                const signature = JSON.stringify(diagnostic());
                if (signature !== diagnosticSignature) {
                    diagnosticSignature = signature;
                    reportEpisodeDiagnostic('playback-state', diagnostic);
                }
            } else {
                diagnosticSignature = undefined;
            }
            const update = args.config.runtimeCallbacks?.onRuntimeStateChange;
            if (!beat || !update) throw new Error('Primitive Episode requires a beat and runtime update callback.');
            const matches = (state: RuntimeState) => isSameEpisodePlaybackExecution(state.episode, playback)
                && state.sceneIndex === args.runtimeState.sceneIndex
                && state.selectedTrace === args.runtimeState.selectedTrace;
            const audition = resolveScenePlanAudition(playback.episodeHandle);
            const developer = process.env.NODE_ENV === 'development'
                && primitiveDeveloperEpisodes.some(episode => episode.handle === playback.episodeHandle);
            const cue = beat.cues?.[playback.cueIndex ?? 0];
            const cueReadyDuringPlayback = scene.sceneKind === 'trace' && cue !== undefined && 'landmark' in cue
                && scene.beats[playback.beatIndex]?.continuousCues?.includes(cue.id) === true
                && (args.runtimeState.episodeVisualStepIndex ?? args.runtimeState.committedStepIndex) >= planAnchorIndex(
                    resolve(args.config, playback.episodeHandle, args.runtimeState.sceneIndex ?? 0).replay, cue);
            return { sceneId: scene.id, beat, cueReadyDuringPlayback, recording: { clips: audition?.clips
                ?? (developer ? developerNarrationManifest.clips : narrationManifest.clips), catalog: 'primitive',
                audioBaseUrl: audition?.audioBaseUrl ?? (developer
                    ? `/narration/developer/primitive/${narrationConfig.production.voice}` : undefined),
                ...(developer && !audition ? { regenerationHint: 'Run pnpm narration:generate --developer --catalog primitive' } : {}),
            },
                update: (reduce: (state: RuntimeState) => RuntimeState) => update(state => matches(state) ? reduce(state) : state),
                onCueRelease: () => {
                    reportEpisodeDiagnostic('cue-release', () => ({ ...diagnostic(),
                        cueId: 'cues' in beat ? beat.cues?.[playback.cueIndex ?? 0]?.id : undefined }));
                },
            };
        },
        continue: (args, state, action) => controller.reduceOverlayIntent?.({ ...args, runtimeState: state,
            intent: { overlayIntentKind: 'action-triggered',
                actionId: action === 'cue' ? 'episode-cue-continue' : 'episode-continue' } }) ?? state,
    }, narration);
}
